import type { Component } from 'vue'
import type { StandardSchemaV1 } from '@tanstack/vue-form'

import type { AssetClass } from '@/entities/assets'
import type { AccountOption } from '@/entities/assets/utils/types'
import type { AssetPriceSource } from '@/entities/assets/components/utils/enums'
import type { TagRef } from '@/entities/tags/utils/types'

import { AssetWizardField, AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

/**
 * How a field's value got there. The spec's whole argument is that this is
 * always visible — the wizard may structure, but it may not invent.
 */
export enum WizardFieldOrigin {
  /** The assistant supplied this. Marked with a sparkle, shown as text on the summary. */
  READ = 'read',
  /** Empty and required. Allposit will not guess it. Marked with an amber dot. */
  REQUIRED = 'required',
  /** A repository default, editable. Marked with a help glyph. */
  DEFAULT = 'default',
  /** Neither required nor from the assistant. */
  OPTIONAL = 'optional',
  /**
   * Fixed by the wizard itself — a loan's type is 'loan'. Shown, never asked
   * for, and carrying no mark: the three marks in the rail's legend are about
   * where a value the user could change came from, and this is not one.
   */
  FIXED = 'fixed',
}

export enum WizardStepState {
  /** Has unfilled required fields; the count says how many. */
  INCOMPLETE = 'incomplete',
  COMPLETE = 'complete',
  /**
   * An optional step the user has not opened yet. Says what it is — something
   * they may do — rather than what they did, because they have not done
   * anything to it.
   */
  OPTIONAL = 'optional',
  /** Left empty, or not built yet. See OPTIONAL for the one nobody has opened. */
  SKIPPED = 'skipped',
}

/** A step as the rail draws it. Derived from the definition and the live values. */
export interface WizardStep {
  key: string
  label: string
  state: WizardStepState
  /** Outstanding required fields, shown on the rail. */
  count?: number
}

/** Which account a record hangs off: one the repository has, or one to create. */
export enum WizardAccountMode {
  EXISTING = 'existing',
  NEW = 'new',
}

/**
 * One investor's slice of a commitment split across more than one entity —
 * shared by private_fund_subscribe_and_commit and private_security, in place
 * of the ownership step's single account. Each becomes its own
 * asset-and-transaction bundle in create(), for its own share — never a
 * field of the generic WizardFieldDef system, the same way `tags` and
 * `prices` are part of the form state without being one.
 */
export interface WizardInvestor {
  accountMode: WizardAccountMode
  /** Set when the read matched an account the repository already has. */
  accountId: string
  accountName: string
  accountType: string
  /** This investor's slice of the commitment. Undefined until entered. */
  share?: number
}

/**
 * What the wizard knows and the answer does not: which accounts this repository
 * actually has.
 *
 * The read is shown every account at once, so an id it returns has to be
 * checked here before anything is filled in with it — the same division the
 * account-suggestion assistant already uses, where the backend guarantees the
 * shape and this side owns the list. An id nobody offered is dropped rather
 * than trusted: pointing the ownership step at an account its own picker cannot
 * show is worse than leaving the step empty.
 */
export interface WizardSeedContext {
  /** What the ownership step can offer — already narrowed to this flow's transaction. */
  ownerAccounts?: AccountOption[]
  /** The types that step allows. One outside them is dropped, not posted. */
  ownerAccountTypes?: string[]
  /** Every account in the repository, for the held-in account's id. */
  accountIds?: string[]
}

/** A required field the wizard is still waiting on. */
export interface WizardMissingField {
  key: string
  label: string
}

/**
 * 'tiles' is a small, mutually exclusive choice drawn as `DSChoiceCard`s in a
 * `DSRadioGroup` rather than a dropdown — Loan's own `direction` field, where
 * there are only ever two options and picking one reads as an answer rather
 * than a lookup. Options come from the same `optionsList`/`options` a
 * `select` field would use; there is no per-option icon or description yet —
 * add one only when a second tiles field actually needs it.
 */
export type WizardControlType = 'text' | 'number' | 'date' | 'select' | 'combobox' | 'tiles'

/**
 * Where a select's options come from. A definition is static; the options are
 * not, so it names a source and the wizard resolves it against the stores.
 */
export type WizardOptionsSource =
  | 'currencies'
  | 'positionAccountTypes'
  | 'liabilityAccountTypes'
  | 'assetTypes'

/**
 * Where an untouched field's value comes from. Same reasoning as
 * `WizardOptionsSource`: the definition names a source, the wizard resolves it.
 *
 * A source rather than a boolean because "falls back to a default" has to be
 * resolvable at the end, not merely announceable in a placeholder — a wizard
 * that promises the repository currency and then creates records with no
 * currency has told the user something untrue.
 */
export type WizardDefaultSource = 'currency' | 'analyticsEndDate'

export interface WizardFieldDef {
  /** Matches the key in the wizard's zod schema and in its form values. */
  name: string
  label: string
  control: WizardControlType
  options?: WizardOptionsSource
  /**
   * Options as values rather than as a source, for a list only the step can
   * resolve — the ownership step's account types depend on which transaction
   * the wizard will post, which a static definition cannot know.
   */
  optionsList?: string[]
  /**
   * An icon per `optionsList`/`options` value, for a `tiles` control only —
   * `DSChoiceCard`'s own icon plate. Keyed by the option string itself rather
   * than carried as a richer option shape: every other control still speaks
   * plain strings, and a tiles field with nothing here just renders without
   * an icon plate rather than failing.
   */
  tileIcons?: Record<string, Component>
  /**
   * A description per `optionsList`/`options` value, for a `tiles` control
   * only — `DSChoiceCard`'s own one-line consequence text. Same reasoning as
   * `tileIcons`: keyed by the option string, optional per option.
   */
  tileDescriptions?: Record<string, string>
  placeholder?: string
  hint?: string
  /**
   * Gates create and carries the amber dot. The schema is what actually
   * enforces it — this is the same fact stated where the rail can count it.
   *
   * A predicate for a field whose requirement depends on another answer: the
   * ownership step needs an account id when picking an existing account and a
   * name and type when creating one, and the rail has to count whichever of
   * those is actually owed.
   */
  required?: boolean | ((values: WizardValues) => boolean)
  /**
   * The assistant's field this maps to, when it can fill it at all. A field
   * without one is the wizard's alone: the assistant never sees it, so it is
   * always the user who supplies it.
   */
  aiField?: AssetWizardField
  /**
   * Left empty, this field resolves to a repository default — named in the
   * placeholder while the wizard is open, and written into the values on
   * submit. See `resolveDefaults`.
   */
  defaulted?: WizardDefaultSource
  /**
   * Columns of the field grid this takes. One unless stated. Two is for a value
   * that is a phrase rather than a figure — a name, a description — where half
   * a row invites an abbreviation. Applied from `sm` up, which is where the
   * grid stops being a single column.
   *
   * Read on the step's own grid, which is two columns wide. The summary's own
   * grid is three — see `summarySpan` for a field that wants a different span
   * there.
   */
  span?: number
  /**
   * `span`'s own value on the summary card instead, when the two grids call
   * for different widths — `span: 2` reads as full width in the step's own
   * two-column grid, but only two of the summary's three; a field that wants
   * full width in both states each explicitly rather than one number trying
   * to mean both. Falls back to `span` when omitted, so a field that wants
   * the same width in either grid states nothing extra.
   */
  summarySpan?: number
}

/** A card on the summary — one record the wizard will create. */
export interface WizardRecordDef {
  key: string
  label: string
  icon: Component
  tone: 'brand' | 'neutral'
  /**
   * The transaction this record creates, e.g. 'add position'. A predicate for
   * a flow whose transaction depends on an earlier answer — Loan asks
   * borrowed-or-lent and posts 'add liability' or 'add position' accordingly,
   * the same reasoning as `WizardFieldDef.required`.
   */
  transactionType: string | ((values: WizardValues) => string)
  /** Field name rendered as the card heading rather than one field among many. */
  heading: string
  fields: string[]
  /** How this record relates to the one above it, e.g. 'secured by'. */
  link?: string
}

/**
 * One recorded value, in form state: an `asset_prices` row before there is an
 * asset to hang it off.
 *
 * `source` is the app's own vocabulary for what kind of mark this is — the two
 * a user may set are Manual and Override, and the difference is what the value
 * does to the timeline afterwards (see ASSET_PRICE_SOURCE_EDITABLE).
 */
export interface WizardMark {
  /** ISO, as the date control reads and writes it. */
  date: string
  /** The value on that date, in the wizard's currency. */
  close: number
  source: AssetPriceSource
  /**
   * Added from the wizard's own purchase price rather than typed. Says so on
   * the row and is dropped before the row is posted — `asset_prices` has no
   * such column, and the value is the same either way.
   */
  fromPurchase?: boolean
}

/**
 * One capital call, fee or distribution against a fund already split across
 * investors — private_fund's own cash-flows step. `investorIndex` is a
 * position into `values.investors`, the same way `barColor`/`:key` already
 * address a row — not a stable id, because a row has none until its account
 * resolves. Removing an investor reindexes or drops the flows that pointed
 * at it; see InvestorsStep.vue's `removeInvestor`.
 */
export interface WizardCashFlow {
  investorIndex: number
  /** ISO, as the date control reads and writes it. */
  date: string
  /** One of PrivateFund's own CONTRIBUTION_TYPES or DISTRIBUTION_TYPES. */
  type: string
  /** The magnitude the user typed — the repository's transaction settings
   * apply the actual sign for this type, the same rule every other amount
   * in these wizards follows. See `requiredAmount`. */
  amount: number
}

export interface WizardStepDef {
  key: string
  /** Short, for the rail. */
  label: string
  /**
   * A word beside the heading, for a step that is not what it looks like — the
   * valuation step reads as one more thing to fill in until it says `Optional`.
   */
  badge?: string
  /**
   * The question the step asks, as its own heading. The rail says where you
   * are; the heading says what is wanted — "Ownership & account" against
   * "Where does this sit?". Falls back to the label.
   */
  heading?: string
  /** One line under the heading on why the step exists. */
  description?: string
  /** What this step collects. A step with no fields is one not built yet. */
  fields?: WizardFieldDef[]
  /**
   * Stands in for a step still being built. The assistant asks for what the
   * step would have collected, so the gap is visible rather than silently
   * missing.
   */
  placeholder?: {
    question: string
    suggestions: string[]
  }
  /** A bespoke body, for a step the field grid cannot express. */
  component?: Component
}

export interface WizardDefinition {
  flow: AssetWizardFlow
  /** What the wizard is called on screen and in the flow picker. */
  label: string
  icon: Component
  /**
   * Validated on change. Typed as the Standard Schema contract rather than as a
   * zod type: that is all TanStack Form consumes, and it keeps this description
   * of a wizard independent of which library wrote the schema.
   */
  schema: StandardSchemaV1<WizardValues>
  steps: WizardStepDef[]
  /** How the summary groups the fields into the records it will create. */
  records: WizardRecordDef[]
  /**
   * Values this flow fixes rather than asks for — a loan's type is 'loan', and
   * choosing the wizard has already said so. They are part of the form state
   * (so the records carry them) but they are not fields: nothing renders them,
   * the rail cannot count them, and the assistant cannot overwrite them.
   */
  constants?: WizardValues
  /**
   * Tags this flow applies on open, whether the repository has the pairs yet
   * or not. A loan is Borrowings · Mortgage the way its type is `loan`: the
   * assistant may name more tags, but these are already selected when the
   * summary opens, even when they still have to be created. Copied into the
   * form — never shared with the definition — so an id resolved against one
   * catalogue cannot leak into the next wizard.
   */
  defaultTags?: TagRef[]
  /**
   * Writes this flow's records, and returns the assets it created — the first
   * being the one the user came for, which is the one the dialog opens.
   *
   * A function per flow rather than something derived from `records`: those
   * describe the cards on the summary, not the calls. Fine art draws two cards
   * for one transaction and a financed property draws two for two, so a card is
   * not a record in the API's sense and never was. Each folder's `create.ts`
   * spells its own steps out, built from `utils/create`.
   *
   * Throws rather than reporting: what a failure looks like is the dialog's to
   * decide, and it has the modal to keep open.
   */
  create: (values: WizardValues) => Promise<AssetClass[]>
}

/**
 * Every wizard's form state: a flat bag of values plus its tags.
 *
 * Strings throughout, except a `number` control's field, which holds a number
 * or `undefined` — see utils/schema. Kept as `any` because the shape is the
 * wizard's zod schema, and that is what validates it.
 */
export type WizardValues = Record<string, any>
